import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api, scopeQuery, useScope } from "../api";
import { useT } from "../i18n";
import { useMe } from "./AdminShell";
import { useAttention } from "./Attention";

interface TodayData {
  today: string;
  week: { prayed: number; sessions: number; checkIns: number; activeUsers: number };
  questions: { open: number; overdue: number };
  scheduledDays: { dailyWords: number; dailyPrayers: number; oneThings: number };
  members: number;
  tasks: { rewardRequests: number; callTasks: number; approvals: number; teamFollowUps: number; openDecisions: number; sentToday: number } | null;
}

export function AdminToday() {
  const { t } = useT();
  const me = useMe();
  const scope = useScope();
  const m = me.data!;
  const churchName = scope.canPickScope ? (scope.churches.find((c) => c.id === scope.churchId)?.name ?? t("admin.scope.global")) : m.church?.name;
  const q = useQuery({ queryKey: ["admin", "today", scope.churchId], queryFn: () => api<TodayData>(`/admin/today${scopeQuery(scope, "church")}`) });
  const d = q.data;
  const att = useAttention();
  const a = att.data;

  return (
    <>
      <p className="eyebrow">{churchName}</p>
      <h1>{t("admin.home.title")}</h1>
      <p className="muted">{t("admin.home.welcome", { name: m.profile.fullName ?? m.profile.alias })}</p>
      {q.isError ? <p className="error">{t("admin.common.error")}</p> : null}

      <h2 className="section">{t("admin.today.week")}</h2>
      <div className="tiles">
        <Tile n={d?.week.prayed} label={t("admin.today.prayed")} />
        <Tile n={d?.week.sessions} label={t("admin.today.sessions")} />
        <Tile n={d?.week.checkIns} label={t("admin.today.checkIns")} />
        <Tile n={d?.week.activeUsers} label={t("admin.today.activeUsers")} />
        <Tile n={d?.members} label={t("admin.today.members")} />
      </div>
      <p className="muted small">
        <Link to="/admin/pulse" className="tile-link">
          {t("admin.today.toPulse")} →
        </Link>
      </p>

      <div className="tiles two">
        <div className={`tile ${a && a.counts.critical > 0 ? "critical" : a && a.counts.overdue > 0 ? "warn" : ""}`}>
          <div className="l">{t("admin.today.attention")}</div>
          <div className="n">{a?.items.length ?? "–"}</div>
          {a ? <div className="muted">{t("admin.today.attentionBody", { count: a.items.length, overdue: a.counts.overdue })}</div> : null}
          <div className="rows">
            {a?.items.slice(0, 4).map((i) => (
              <Row key={i.id} k={t(`admin.attention.kind.${i.kind}`)} v={i.detail ?? i.title} />
            ))}
          </div>
          <Link to="/admin/attention" className="tile-link">
            {t("admin.today.toAttention")} →
          </Link>
        </div>
        <div className={`tile ${d && d.questions.overdue > 0 ? "warn" : ""}`}>
          <div className="l">{t("admin.today.openQuestions")}</div>
          <div className="n">{d?.questions.open ?? "–"}</div>
          {d && d.questions.overdue > 0 ? <div className="error">{t("admin.today.overdue", { n: d.questions.overdue })}</div> : null}
          <Link to="/admin/questions" className="tile-link">
            {t("admin.today.toQuestions")} →
          </Link>
        </div>
        {d?.tasks ? (
          <div className={`tile ${Object.entries(d.tasks).some(([k, n]) => k !== "sentToday" && n > 0) ? "warn" : ""}`}>
            <div className="l">{t("admin.today.tasks")}</div>
            <div className="rows">
              {d.tasks.rewardRequests > 0 ? <TaskRow label={t("admin.today.rewardRequests", { n: d.tasks.rewardRequests })} to="/admin/rewards" link={t("admin.today.toRewards")} /> : null}
              {d.tasks.callTasks > 0 || d.tasks.openDecisions > 0 ? <TaskRow label={[d.tasks.openDecisions > 0 ? t("admin.today.openDecisions", { n: d.tasks.openDecisions }) : null, d.tasks.callTasks > 0 ? t("admin.today.callTasks", { n: d.tasks.callTasks }) : null].filter(Boolean).join(" · ")} to="/admin/decisions" link={t("admin.today.toDecisions")} /> : null}
              {d.tasks.approvals > 0 ? <TaskRow label={t("admin.today.approvals", { n: d.tasks.approvals })} to="/admin/people" link={t("admin.today.toPeople")} /> : null}
              {d.tasks.teamFollowUps > 0 ? <TaskRow label={t("admin.today.teamFollowUps", { n: d.tasks.teamFollowUps })} to="/admin/team" link={t("admin.today.toTeam")} /> : null}
            </div>
            <div className="muted small" style={{ marginTop: 8 }}>
              {t("admin.today.sentToday")}: {t("admin.today.sentTodayBody", { n: d.tasks.sentToday })}
            </div>
          </div>
        ) : null}
        <div className="tile">
          <div className="l">{t("admin.today.scheduled")}</div>
          <div className="rows">
            <Row k={t("admin.today.dailyWords")} v={d ? t("admin.today.days", { n: d.scheduledDays.dailyWords }) : "–"} />
            <Row k={t("admin.today.dailyPrayers")} v={d ? t("admin.today.days", { n: d.scheduledDays.dailyPrayers }) : "–"} />
            <Row k={t("admin.today.oneThings")} v={d ? t("admin.today.days", { n: d.scheduledDays.oneThings }) : "–"} />
          </div>
          <Link to="/admin/content" search={{ tab: "dailyWords" }} className="tile-link">
            {t("admin.today.toContent")} →
          </Link>
        </div>
      </div>
    </>
  );
}

function Tile({ n, label }: { n: number | undefined; label: string }) {
  return (
    <div className="tile">
      <div className="l">{label}</div>
      <div className="n">{n ?? "–"}</div>
    </div>
  );
}

function TaskRow({ label, to, link }: { label: string; to: "/admin/rewards" | "/admin/decisions" | "/admin/people" | "/admin/team"; link: string }) {
  return (
    <div className="row">
      <span>{label}</span>
      <Link to={to} className="tile-link">
        {link} →
      </Link>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="row">
      <span>{k}</span>
      <strong>{v}</strong>
    </div>
  );
}
